import React, { memo, useCallback, useMemo, useRef, type RefObject } from 'react';
import { StyleSheet, View } from 'react-native';
import Map, {
  type MapRef,
  type ViewStateChangeEvent,
} from 'react-map-gl/maplibre';
import type { CameraRef } from '@maplibre/maplibre-react-native';

import StationMarker from './StationMarker';
import type { Station } from '../types/station';
import type { Coordinates } from '../utils/geolocation';
import { INITIAL_ZOOM, createMapStyle } from '../utils/mapConfig';

type StationMapViewProps = {
  cameraRef: RefObject<CameraRef | null>;
  // The web map renders from the style in `src/config/webMapStyle.json` plus
  // the tile URL in `src/config/tilesConfig.web.ts` (see `mapConfig.web.ts`),
  // so the local mbtiles path the native component uses is ignored. It is still
  // forwarded to `createMapStyle` to keep this component's props in parity with
  // the native one.
  tilePath: string;
  stations: Station[];
  longitude: number;
  latitude: number;
  onMapPress: () => void;
  onStationPress: (station: Station) => void;
  onCenterChange: (center: Coordinates) => void;
};

/**
 * Web counterpart of the native MapLibre <Map>: renders the map with
 * react-map-gl + maplibre-gl.
 *
 * The map instance exposes the same imperative flyTo() surface that MapWrapper
 * expects from a native CameraRef, so we bridge it through the shared
 * `cameraRef` prop.
 *
 * Like the native version, this component is memoized so that selecting /
 * deselecting a station (which re-renders MapWrapper) never re-renders the map
 * or the markers.
 */
const StationMapView = memo(
  ({
    cameraRef,
    tilePath,
    stations,
    longitude,
    latitude,
    onMapPress,
    onStationPress,
    onCenterChange,
  }: StationMapViewProps) => {
    const mapRef = useRef<MapRef | null>(null);

    // Report the center the user is looking at; `onMove` fires while panning,
    // zooming or animated flyTos happen (the caching hook throttles the writes).
    const handleMove = useCallback(
      (event: ViewStateChangeEvent) => {
        const {
          latitude: eventLatitude,
          longitude: eventLongitude,
        } = event.viewState;

        onCenterChange({ latitude: eventLatitude, longitude: eventLongitude });
      },
      [onCenterChange],
    );

    // Built through the shared `createMapStyle` so both platforms expose the
    // same API; on the web the tile path is ignored. The vector source is
    // capped at z13 (the tile server's deepest zoom) so that zooming past it
    // keeps drawing the over-zoomed tiles instead of requesting z15+ tiles the
    // server doesn't have (which would leave the map blank).
    const mapStyle = useMemo(() => {
      const style = createMapStyle(tilePath);

      return {
        ...style,
        sources: Object.fromEntries(
          Object.entries(style.sources).map(([id, source]) => [
            id,
            { ...source, maxzoom: 13 },
          ]),
        ),
      };
    }, [tilePath]);

    // Bridge react-map-gl's MapRef into the shared CameraRef-shaped prop.
    const handleMapRef = useCallback(
      (instance: MapRef | null) => {
        mapRef.current = instance;
        cameraRef.current = (instance as unknown as CameraRef) ?? null;
      },
      [cameraRef],
    );

    const initialViewState = useMemo(
      () => ({ longitude, latitude, zoom: INITIAL_ZOOM }),
      [longitude, latitude],
    );

    const handleMapClick = useCallback(() => {
      onMapPress();
    }, [onMapPress]);

    return (
      <View style={styles.container}>
        <Map
          ref={handleMapRef}
          mapStyle={
            mapStyle as unknown as React.ComponentProps<typeof Map>['mapStyle']
          }
          initialViewState={initialViewState}
          minZoom={4}
          maxZoom={20}
          // Like the native map (`logo={false}` + `attribution={false}` in
          // StationMapView.tsx), hide the MapLibre logo and the attribution
          // control (which otherwise renders the MapLibre wordmark + style
          // attribution) that maplibre-gl adds by default.
          attributionControl={false}
          maplibreLogo={false}
          onClick={handleMapClick}
          onMove={handleMove}
          style={styles.map}
        >
          {stations.map(station => (
            <StationMarker
              key={station.name}
              station={station}
              onPress={onStationPress}
            />
          ))}
        </Map>
      </View>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  map: {
    width: '100%',
    height: '100%',
  },
});

export default StationMapView;
